// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

export interface ProductDetailsViewStyles {
    container?: InjectableCss;
    notification?: InjectableCss;
    productImagoryAndDetails?: InjectableCss;
    imagoryWrapper?: InjectableCss;
    imageCarousel?: InjectableCss;
    primaryImage?: InjectableCss;
    detailsWrapper?: InjectableCss;
    productNumbersWrapper?: InjectableCss;
    priceInventoryQrCode?: InjectableCss;
    priceWrapper?: InjectableCss;
    inventoryWrapper?: InjectableCss;
    qrCode?: InjectableCss;
    productActions?: InjectableCss;
    qtyOrdredWrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    addTolistWrapper?: InjectableCss;
    specifications?: InjectableCss;
    suggestedProducts?: InjectableCss;
}

export const viewStyles: ProductDetailsViewStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            &&& div[class*="ZoneStyle-"] {
                height: auto;
                width: auto;
            }
        `,
    },
    notification: {
        css: css`
            margin-bottom: 30px;
            margin-top: 15px;
        `,
    },
    productImagoryAndDetails: {
        css: css`
            margin-bottom: 75px;
            display: flex;
            flex-wrap: wrap;
        `,
    },
    imagoryWrapper: {
        css: css`
            width: 550px;
            display: flex;
            justify-content: space-between;
            @media (max-width: 1200px) {
                width: 520px;
            }

            @media (max-width: 1024px) {
                width: 100%;
                margin-bottom: 50px;
            }

            @media (max-width: 760px) {
                flex-wrap: wrap;
            }
        `,
    },
    imageCarousel: {
        css: css`
            width: 88px;
            padding-top: 25px;
            @media (max-width: 760px) {
                width: 100%;
                padding-top: 0;
            }
        `,
    },
    primaryImage: {
        css: css`
            width: calc(100% - 115px);
            margin-left: 27px;

            &&.single {
                width: 100%;
                margin: 0;
            }

            @media (max-width: 1024px) {
                &&& > div[class*="Wrapper-sc"] {
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
            }
            @media (max-width: 760px) {
                width: 100%;
                margin-left: 0;
                margin-top: 10px;
            }
        `,
    },
    detailsWrapper: {
        css: css`
            width: calc(100% - 580px);
            margin-left: 30px;
            display: flex;
            flex-direction: column;
            @media (max-width: 1200px) {
                width: calc(100% - 540px);
                margin-left: 20px;
            }
            @media (max-width: 1024px) {
                width: 100%;
                margin-left: 0;
            }
        `,
    },
    productNumbersWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            padding-bottom: 20px;
            margin-bottom: 17px;
            border-bottom: 1px solid ${getColor("common.ui2")};
            && div[class*="Wrapper-"] {
                flex-grow: 1;
                width: auto;
            }
        `,
    },
    priceInventoryQrCode: {
        css: css`
            display: flex;
            align-items: flex-end;
            margin-bottom: 15px;
            flex-wrap: wrap;
        `,
    },
    priceWrapper: {
        css: css`
            margin-right: 15px;
        `,
    },
    qrCode: {
        css: css`
            flex: 1;
            display: flex;
            justify-content: flex-end;

            @media (max-width: 481px) {
                width: 100%;
                justify-content: flex-start;
                margin-top: 10px;
            }
        `,
    },
    productActions: {
        css: css`
            display: flex;
            @media (max-width: 450px) {
                flex-wrap: wrap;
            }
            @media print {
                display: none;
            }
        `,
    },
    qtyOrdredWrapper: {
        css: css`
            max-width: 130px;
            margin-right: 10px;
            @media (max-width: 320px) {
                margin-bottom: 10px;
            }
        `,
    },
    addToCartWrapper: {
        css: css`
            margin-right: 23px;
        `,
    },
    addTolistWrapper: {
        css: css`
            display: flex;
            align-items: center;
            @media (max-width: 450px) {
                margin-top: 7px;
            }
        `,
    },
};

const styles = viewStyles;

const ProductDetailsView: React.FC<WidgetProps & HasProduct> = props => {
    const { product } = props;
    if (!product) {
        return (
            <StyledWrapper
                css={css`
                    height: 300px;
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                `}
            >
                <LoadingSpinner />
            </StyledWrapper>
        );
    }
    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.notification}>
                <Zone contentId={props.id} zoneName="suggestedNotificationZone" />
            </StyledWrapper>
            <StyledWrapper {...styles.productImagoryAndDetails}>
                <StyledWrapper {...styles.imagoryWrapper}>
                    {product.images && product.images?.length > 1 && (
                        <StyledWrapper {...styles.imageCarousel}>
                            <Zone contentId={props.id} zoneName="imageCarousel" />
                        </StyledWrapper>
                    )}
                    <StyledWrapper
                        {...styles.primaryImage}
                        className={!product.images || product.images?.length < 2 ? "single" : ""}
                    >
                        <Zone contentId={props.id} zoneName="primareyImage" />
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.detailsWrapper}>
                    <Zone contentId={props.id} zoneName="productTitleZone" />
                    <StyledWrapper {...styles.productNumbersWrapper}>
                        <Zone contentId={props.id} zoneName="mfrNumberZone" />
                        <Zone contentId={props.id} zoneName="partNumberZone" />
                        <Zone contentId={props.id} zoneName="brandNameZone" />
                        <Zone contentId={props.id} zoneName="upcNumberZone" />
                    </StyledWrapper>
                    <Zone contentId={props.id} zoneName="productDescriptionAndBadgesZone" />
                    <StyledWrapper {...styles.priceInventoryQrCode}>
                        <StyledWrapper {...styles.priceWrapper}>
                            <Zone contentId={props.id} zoneName="productPriceZone" />
                        </StyledWrapper>
                        <StyledWrapper {...styles.inventoryWrapper}>
                            <Zone contentId={props.id} zoneName="ProductAvailabilityZone" />
                        </StyledWrapper>
                        <StyledWrapper {...styles.qrCode}>
                            <Zone contentId={props.id} zoneName="productQrCodeZoneZone" />
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.productActions}>
                        <StyledWrapper {...styles.qtyOrdredWrapper}>
                            <Zone contentId={props.id} zoneName="productQtyOrderedZone" />
                        </StyledWrapper>
                        <StyledWrapper {...styles.addToCartWrapper}>
                            <Zone contentId={props.id} zoneName="productAddToCartZone" />
                        </StyledWrapper>
                        <StyledWrapper {...styles.addTolistWrapper}>
                            <Zone contentId={props.id} zoneName="productAddToListZone" />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper>
                <Zone contentId={props.id} zoneName="productSpecificationsZone" />
            </StyledWrapper>
            <StyledWrapper id="productSuggestions">
                <Zone contentId={props.id} zoneName="productsuggestedNextZone" />
            </StyledWrapper>
            <StyledWrapper>
                <Zone contentId={props.id} zoneName="productCarouselsZone" />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsView),
    definition: {
        displayName: "View",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
